import { setupClass, makeEnum, } from '../Base.js'
import { Field, FieldType, ProjectUnits, } from '../Common/Field.js'
import { InputComponent } from '../Common/InputComponent.js'
import { ObjectVisitor } from '../Common/ObjectVisitor.js'
import { Updater } from '../Common/Updater.js'
import { Watcher } from '../Common/Watcher.js'
import { LocalStorageSaver } from '../Common/LocalStorageSaver.js'

/**
 * Enum for new fan state type selection
 */
let NewFanStateType = makeEnum({
  NewSpeed: 'New Speed',
  NewPressureDrop: 'New Pressure Drop',
  NewAirflow: 'New Airflow',
  NewPower: 'New Power',
})

/**
 * AffinityLawsCalculator
 * Calculates fan/pump affinity laws relationships
 */
export class AffinityLawsCalculator extends InputComponent {
  init() {
    // We can't use the project watcher here, because the calculator is at
    // the global level.
    this.watcher = Watcher.globalInstance();
    this.updater.setWatcher(this.watcher);
    console.log('[AffinityLawsCalculator] Initializing calculator');

    this.units = Field.makeSelect('Units', ProjectUnits, { watcher: this.watcher })
    this.units.value = 'Imperial'
    console.log('[AffinityLawsCalculator] Units field created:', this.units)

    // Create a project context that fields can use to determine display units
    this.projectContext = {
      getProjectUnits: () => this.units.value
    }

    // Inputs
    this.airflow = new Field({
      name: 'Airflow',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1000,
    })
    console.log('[AffinityLawsCalculator] Airflow field created:', this.airflow)

    this.staticPressure = new Field({
      name: 'Static Pressure',
      type: FieldType.SmallPressure,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1.0,
    })
    console.log('[AffinityLawsCalculator] StaticPressure field created:', this.staticPressure)

    this.fanEfficiency = new Field({
      name: 'Fan Efficiency',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 75,
    })
    console.log('[AffinityLawsCalculator] FanEfficiency field created:', this.fanEfficiency)

    this.altitude = new Field({
      name: 'Altitude',
      type: FieldType.Length,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0,
      min: 0,
      allowMin: true,
    })
    console.log('[AffinityLawsCalculator] Altitude field created:', this.altitude)

    this.fanSpeed = new Field({
      name: 'Fan Speed',
      type: FieldType.SpinSpeed,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1800,
    })
    console.log('[AffinityLawsCalculator] FanSpeed field created:', this.fanSpeed)

    // Output
    this.brakePower = new Field({
      name: 'Brake Power',
      type: FieldType.Power,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[AffinityLawsCalculator] BrakePower field created:', this.brakePower)
    this.brakePower.makeUpdater(() => {
      this.calculateBrakePower();
    });

    // State 2 (Affinity Laws) - Inputs
    this.newFanStateType = Field.makeSelect('Set by:', NewFanStateType, { watcher: this.watcher })
    this.newFanStateType.value = 'NewSpeed'
    console.log('[AffinityLawsCalculator] NewFanStateType field created:', this.newFanStateType)

    this.inputFanSpeed = new Field({
      name: 'New Fan Speed',
      type: FieldType.SpinSpeed,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1800,
    })
    this.inputFanSpeed.setVisibility(() => this.newFanStateType.value === 'NewSpeed')

    this.inputStaticPressure = new Field({
      name: 'New Static Pressure',
      type: FieldType.SmallPressure,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1.0,
    })
    this.inputStaticPressure.setVisibility(() => this.newFanStateType.value === 'NewPressureDrop')

    this.inputAirflow = new Field({
      name: 'New Airflow',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1000,
    })
    this.inputAirflow.setVisibility(() => this.newFanStateType.value === 'NewAirflow')

    this.inputBrakePower = new Field({
      name: 'New Brake Power',
      type: FieldType.Power,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1.0,
    })
    this.inputBrakePower.setVisibility(() => this.newFanStateType.value === 'NewPower')

    // State 2 (Affinity Laws) - Outputs
    this.newAirflow = new Field({
      name: 'New Airflow',
      type: FieldType.AirFlow,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[AffinityLawsCalculator] NewAirflow field created:', this.newAirflow)

    this.newStaticPressure = new Field({
      name: 'New Static Pressure',
      type: FieldType.SmallPressure,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[AffinityLawsCalculator] NewStaticPressure field created:', this.newStaticPressure)

    this.newFanSpeed = new Field({
      name: 'New Fan Speed',
      type: FieldType.SpinSpeed,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[AffinityLawsCalculator] NewFanSpeed field created:', this.newFanSpeed)

    this.newBrakePower = new Field({
      name: 'New Brake Power',
      type: FieldType.Power,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[AffinityLawsCalculator] NewBrakePower field created:', this.newBrakePower)
    this.newAirflow.makeUpdater(() => {
      this.calculateAffinityLaws();
    });

    // Set up units updater to sync display values when units change
    this.prevUnits = this.units.value;
    this.units.makeUpdater((field) => {
      if (this.units.value !== this.prevUnits) {
        // Trigger a units update in all child points
        console.log(`Handling project units change`);
        let objectVisitor = new ObjectVisitor(this);
        objectVisitor.visit((obj, data) => {
          if (obj instanceof Field) {
            console.log(`Calling onProjectUnitsChanged for field at ${data.curPath}: ${obj.name}`);
            obj.onProjectUnitsChanged(field.value);
          }
        })
      }
      this.prevUnits = this.units.value;
    })

    // For auto-saving
    this.saver = new LocalStorageSaver('heatwise-affinity-laws-calculator', this);
    
    // Set up fields for serialization
    this.serFields = [
      'units',
      'airflow',
      'staticPressure',
      'fanEfficiency',
      'altitude',
      'fanSpeed',
      'newFanStateType',
      'inputFanSpeed',
      'inputStaticPressure',
      'inputAirflow',
      'inputBrakePower',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'AffinityLaws',
      path: 'affinity-laws',
    }
  }

  calculateBrakePower() {
    try {
      console.log('[AffinityLawsCalculator] calculateBrakePower() called');
      const airflow = this.airflow.value
      const pressure = this.staticPressure.value
      const efficiency = this.fanEfficiency.value / 100  // Convert percent to decimal
      const altitude = this.altitude.value

      console.log(`[AffinityLawsCalculator] Input values: airflow=${airflow}, pressure=${pressure}, efficiency=${efficiency}, altitude=${altitude}`);

      // Calculate density ratio based on altitude
      const densityRatio = this.getDensityRatio(altitude)
      console.log(`[AffinityLawsCalculator] Density ratio: ${densityRatio}`);

      // Always use imperial formula internally - all field values are stored in imperial units
      // (CFM, in.w.c., feet, etc.)
      // Imperial: BHP = (CFM × Static Pressure × Density Ratio) / (6356 × Efficiency)
      const bhp = (airflow * pressure * densityRatio) / (6356 * efficiency)
      console.log(`[AffinityLawsCalculator] Calculated BHP: ${bhp}`);
      this.brakePower.value = bhp
    } catch (err) {
      console.error('[AffinityLawsCalculator] Error in calculateBrakePower:', err);
    }
  }

  /**
   * Calculate density ratio at given altitude
   * Uses simplified standard atmosphere model
   * Always expects altitude in feet (as stored internally)
   */
  getDensityRatio(altitude) {
    // altitude in feet
    // Standard atmosphere: pressure ratio = (1 - 0.0000068756 * altitude_ft)^5.2558
    const pressureRatio = Math.pow(1 - 0.0000068756 * altitude, 5.2558)
    return pressureRatio
  }

  /**
   * Calculate new fan parameters using affinity laws
   * Based on user selection of which parameter changed
   */
  calculateAffinityLaws() {
    try {
      console.log('[AffinityLawsCalculator] calculateAffinityLaws() called');
      
      // Get state 1 values
      const Q1 = this.airflow.value;
      const P1 = this.staticPressure.value;
      const N1 = this.fanSpeed.value;
      const BHP1 = this.brakePower.value;
      const newStateType = this.newFanStateType.value;

      console.log(`[AffinityLawsCalculator] State 1: Q1=${Q1}, P1=${P1}, N1=${N1}, BHP1=${BHP1}`);
      console.log(`[AffinityLawsCalculator] New state type: ${newStateType}`);

      let Q2, P2, N2, BHP2, ratio;
      switch(newStateType) {
        case 'NewSpeed':
          // Given new speed, calculate others
          N2 = this.inputFanSpeed.value;
          ratio = N2 / N1;
          Q2 = Q1 * ratio;
          P2 = P1 * ratio * ratio;  // P2 = P1 * (N2/N1)²
          BHP2 = BHP1 * ratio * ratio * ratio;  // BHP2 = BHP1 * (N2/N1)³
          break;

        case 'NewPressureDrop':
          // Given new pressure, calculate others
          P2 = this.inputStaticPressure.value;
          ratio = Math.sqrt(P2 / P1);  // ratio = √(P2/P1)
          N2 = N1 * ratio;
          Q2 = Q1 * ratio;
          BHP2 = BHP1 * ratio * ratio * ratio;  // BHP2 = BHP1 * (P2/P1)^1.5
          break;

        case 'NewAirflow':
          // Given new airflow, calculate others
          Q2 = this.inputAirflow.value;
          ratio = Q2 / Q1;
          N2 = N1 * ratio;
          P2 = P1 * ratio * ratio;  // P2 = P1 * (Q2/Q1)²
          BHP2 = BHP1 * ratio * ratio * ratio;  // BHP2 = BHP1 * (Q2/Q1)³
          break;

        case 'NewPower':
          // Given new power, calculate others
          BHP2 = this.inputBrakePower.value;
          ratio = Math.cbrt(BHP2 / BHP1);  // ratio = ∛(BHP2/BHP1)
          N2 = N1 * ratio;
          Q2 = Q1 * ratio;
          P2 = P1 * ratio * ratio;  // P2 = P1 * (BHP2/BHP1)^(2/3)
          break;

        default:
          console.error('[AffinityLawsCalculator] Unknown new fan state type:', newStateType);
          return;
      }

      console.log(`[AffinityLawsCalculator] State 2: Q2=${Q2}, P2=${P2}, N2=${N2}, BHP2=${BHP2}`);

      // Set output values
      this.newAirflow.value = Q2;
      this.newStaticPressure.value = P2;
      this.newFanSpeed.value = N2;
      this.newBrakePower.value = BHP2;

    } catch (err) {
      console.error('[AffinityLawsCalculator] Error in calculateAffinityLaws:', err);
    }
  }

  getProjectUnits() {
    return this.units.value;
  }

  loadFromStorage() {
    this.saver.loadFromStorage();
  }

  async save() {
    await this.saver.save();
  }
}
setupClass(AffinityLawsCalculator)
